import { Directive, ElementRef, OnDestroy, OnInit, inject } from '@angular/core';
import { DatePicker } from 'primeng/datepicker';
import { markHollowEscapeClaim } from '@branduals/dialog-guard';

/**
 * Makes Escape honest on an INLINE PrimeNG date picker. In `[inline]` mode there is no overlay,
 * yet every panel Escape arm (day/month/year cells) still preventDefaults — a structural claim
 * that can never do anything useful, so every press through the panel would reach the document
 * ladder pre-claimed and go dead. The one inline mount in the app sits in the Time-travel panel,
 * where Escape must reach the ladder over the panel.
 *
 * Every Escape crossing this host is therefore tagged as a hollow claim — always, because in
 * inline mode every claim IS hollow by construction (this is decidable from the widget alone; a
 * popup-mode picker guards its input-leg claim on the overlay and needs no tag). Same contract
 * as the autocomplete tagger: a FACT, never a decision — no preventing, no stopping, no acting.
 */
@Directive({
  selector: '[appDatepickerEscapeTag]',
  standalone: true,
})
export class DatepickerEscapeTagDirective implements OnInit, OnDestroy {
  private readonly el = inject(ElementRef<HTMLElement>);
  // Mount assertion only: restricts the selector to a real date-picker host at DI time, so a
  // stray mount on some other element cannot silently hollow-tag genuine claims.
  private readonly host = inject(DatePicker, { self: true });

  private readonly handler = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') return;
    markHollowEscapeClaim(event);
  };

  ngOnInit(): void {
    // Capture phase, like the autocomplete tagger — the tag must land before any bubble-phase
    // reader sees the press.
    this.el.nativeElement.addEventListener('keydown', this.handler, { capture: true });
  }

  ngOnDestroy(): void {
    this.el.nativeElement.removeEventListener('keydown', this.handler, { capture: true });
  }
}
